/* eslint-disable import/no-cycle */
import React from 'react';
import asyncComponent from 'util/asyncComponent';
import AdminContainer from 'containers/admin/AdminContainer';
import AppContainer from 'containers/app/AppContainer';
import Settings from 'pages/app/settings/Settings';
import {
  ROLE_ACCOUNTANT,
  ROLE_ADMIN,
  ROLE_CLINIC_ADMINISTRATOR,
  ROLE_FINANCIAL,
  ROLE_FINANCIAL_ADMINISTRATOR,
  ROLE_OTHER_HEALTH_PROFESSIONAL,
  ROLE_PARTNERSHIP,
  ROLE_PROFESSIONAL,
  ROLE_RECEPTIONIST,
  ROLE_STOCK,
  ROLE_STUDENT,
  SUBSCRIPTION_MANAGER_ROLES
} from 'constants/types';
import PartnershipContainer from 'containers/partnership/PartnershipContainer';
import useSegmentField from 'core/hooks/useSegmentField';

const useConfig = () => {
  const segmentFields = useSegmentField();

  const getServiceRoutes = () => {
    const icon = segmentFields.isHealthcareEnabled ? 'procedures' : 'briefcase';

    return {
      roles: [ROLE_CLINIC_ADMINISTRATOR],
      path: '/procedures',
      title: `${segmentFields.services}`,
      singularTitle: `${segmentFields.service}`,
      component: asyncComponent(() =>
        import('pages/app/procedures/ProcedureList')
      ),
      menu: { icon }
    };
  };

  const getRecordSectionsRoutes = () => {
    return {
      roles: [
        ROLE_CLINIC_ADMINISTRATOR,
        ROLE_PROFESSIONAL,
        ROLE_STUDENT,
        ROLE_OTHER_HEALTH_PROFESSIONAL
      ],
      path: '/record-sections',
      title: 'Seções',
      singularTitle: 'Seção',
      component: asyncComponent(() =>
        import('pages/app/medicalRecordSections/MedicalRecordSectionList')
      ),
      menu: { icon: 'clipboard-list' }
    };
  };

  const getPrescriptionRoutes = () => {
    return {
      roles: [ROLE_CLINIC_ADMINISTRATOR],
      path: '/prescription-models',
      title: 'Modelos de prescrição',
      singularTitle: 'Modelo de prescrição',
      component: asyncComponent(() =>
        import('pages/app/prescriptionModels/PrescriptionModelList')
      ),
      menu: { icon: 'briefcase-medical' }
    };
  };

  const getRecordRouteItems = () => {
    const items = [];
    items.push(getRecordSectionsRoutes());

    if (segmentFields.isHealthcareEnabled) {
      items.push(getPrescriptionRoutes());
    }

    return items;
  };

  const getRecordRoutes = () => {
    const childRoutes = getRecordRouteItems();

    if (childRoutes > 1) {
      return {
        roles: [
          ROLE_CLINIC_ADMINISTRATOR,
          ROLE_PROFESSIONAL,
          ROLE_STUDENT,
          ROLE_OTHER_HEALTH_PROFESSIONAL
        ],
        path: '/record',
        title: `${segmentFields.record}`,
        component: () => null,
        menu: { icon: 'notes-medical' },
        routes: getRecordRouteItems()
      };
    }

    return {
      roles: [ROLE_CLINIC_ADMINISTRATOR],
      path: `/record${childRoutes[0].path}`,
      title: childRoutes[0].title,
      singularTitle: childRoutes[0].singularTitle,
      component: childRoutes[0].component,
      menu: {
        icon: segmentFields.isHealthcareEnabled ? 'notes-medical' : 'book'
      }
    };
  };

  return {
    roles: [
      {
        name: ROLE_ADMIN,
        defaultPath: '/a/accounts/professional-accounts'
      },
      {
        name: '*',
        defaultPath: '/app'
      },
      {
        name: ROLE_PARTNERSHIP,
        defaultPath: '/p'
      },
      {
        name: ROLE_FINANCIAL,
        defaultPath: '/app/financial/financial-dashboard'
      },
      {
        name: ROLE_FINANCIAL_ADMINISTRATOR,
        defaultPath: '/app/financial/financial-dashboard'
      },
      {
        name: ROLE_ACCOUNTANT,
        defaultPath: '/app/settings/accountings'
      }
    ],
    routes: [
      {
        path: '/signin',
        component: asyncComponent(() => import('pages/sign/SignIn'))
      },
      {
        path: '/signup',
        component: asyncComponent(() => import('pages/sign/SignUp'))
      },
      {
        path: '/partnerships/signup',
        component: asyncComponent(() => import('pages/sign/SignUpPartnership'))
      },
      {
        path: '/signout',
        component: asyncComponent(() => import('pages/sign/SignOut'))
      },
      {
        path: '/sign-forgot-password',
        component: asyncComponent(() => import('pages/sign/SignForgotPassword'))
      },
      {
        path: '/sign-reset-password',
        component: asyncComponent(() => import('pages/sign/SignResetPassword'))
      },
      {
        path: '/accounts-chooser',
        title: 'Alterar',
        component: asyncComponent(() =>
          import('pages/shared/TenantOrCompanyChooser')
        ),
        menu: { icon: 'th-large' }
      },
      {
        path: '/profile/:token',
        component: asyncComponent(() =>
          import('../pages/app/accountSharings/AccountSharingProfile')
        )
      },
      {
        path: '/request-share',
        component: asyncComponent(() =>
          import('../pages/public/RequestShareQRCode')
        )
      },
      {
        path: '/collaborator-invitation',
        component: asyncComponent(() =>
          import('pages/shared/CollaboratorInvitation')
        )
      },
      {
        path: '/partnerships-invitation',
        component: asyncComponent(() =>
          import('pages/shared/PartnershipInvitation')
        )
      },
      {
        path: '/partnerships-subscription-invitation',
        component: asyncComponent(() =>
          import('pages/shared/PartnershipSubscriptionInvitation')
        )
      },
      {
        path: '/support',
        component: asyncComponent(() => import('pages/public/RequestSupport'))
      },

      {
        path: '/subscription-success',
        component: asyncComponent(() =>
          import('pages/app/subscriptions/SubscriptionSuccess')
        )
      },
      {
        path: '/upload',
        component: asyncComponent(() => import('pages/public/MobileUploadPage'))
      },
      {
        path: '/a',
        component: (props) => <AdminContainer {...props} />,
        roles: [ROLE_ADMIN],
        routes: [
          {
            path: '/accounts',
            title: 'Contas',
            singularTitle: 'Conta',
            menu: { icon: 'user' },
            exact: true,
            routes: [
              {
                path: '/professional-accounts',
                accountType: 'professional',
                resource: 'professional-accounts',
                endpoint: 'professional-accounts/accounts-with-details',
                title: `${segmentFields.serviceProviders}`,
                singularTitle: `${segmentFields.serviceProvider}`,
                component: asyncComponent(() =>
                  import('pages/admin/accounts/AccountList')
                ),
                menu: { icon: 'user' }
              },
              {
                path: '/student-accounts',
                accountType: 'student',
                resource: 'student-accounts',
                endpoint: 'student-accounts/accounts-with-details',
                title: 'Estudantes',
                singularTitle: 'Estudante',
                component: asyncComponent(() =>
                  import('pages/admin/accounts/AccountList')
                ),
                menu: { icon: 'user' }
              },
              {
                path: '/user-accounts',
                accountType: 'user',
                resource: 'user-accounts',
                endpoint: 'user-accounts/accounts-with-details',
                title: 'Usuários',
                singularTitle: 'Usuário',
                component: asyncComponent(() =>
                  import('pages/admin/accounts/AccountList')
                ),
                menu: { icon: 'user' }
              }
            ]
          },
          {
            path: '/packages',
            resource: 'packages',
            title: 'Pacotes',
            singularTitle: 'Pacote',
            component: asyncComponent(() =>
              import('pages/admin/packages/PackageList')
            ),
            menu: { icon: 'box' }
          },
          {
            path: '/subscription-plans',
            title: 'Planos',
            singularTitle: 'Plano',
            component: asyncComponent(() =>
              import('pages/admin/subscriptionPlans/SubscriptionPlanList')
            ),
            menu: { icon: 'hand-holding-usd' }
          },
          {
            path: '/segments',
            title: 'Segmentos',
            singularTitle: 'Segmento',
            component: asyncComponent(() =>
              import('pages/admin/segments/SegmentList')
            ),
            menu: { icon: 'box' }
          },
          {
            path: '/partnerships',
            resource: 'partnerships',
            title: 'Parceiros',
            singularTitle: 'Parceiro',
            component: asyncComponent(() =>
              import('pages/admin/partnerships/PartnershipList')
            ),
            menu: { icon: 'box' }
          }
        ]
      },
      {
        path: '/p',
        component: (props) => <PartnershipContainer {...props} />,
        roles: [ROLE_PARTNERSHIP],
        routes: [
          {
            roles: [ROLE_PARTNERSHIP],
            path: '/licenses',
            title: 'Licenças',
            singularTitle: 'Licença',
            resource: 'partnerships/subscriptions',
            menu: { icon: 'id-badge' },
            exact: true,
            component: asyncComponent(() =>
              import('pages/partnerships/licenses/LicenseList')
            )
          },
          {
            roles: [ROLE_PARTNERSHIP],
            path: '/subscriptions',
            title: 'Assinatura',
            exact: true,
            component: asyncComponent(() =>
              import('pages/app/subscriptions/Subscription')
            )
          },
          {
            roles: [ROLE_PARTNERSHIP],
            path: '/current-subscriptions',
            title: 'Minha assinatura',
            exact: true,
            component: asyncComponent(() =>
              import('pages/app/subscriptions/SubscriptionDetail')
            )
          }
        ]
      },
      {
        path: '/app',
        component: (props) => <AppContainer {...props} />,
        roles: ['*'],
        routes: [
          {
            path: '/',
            title: 'Visão geral',
            exact: true,
            component: asyncComponent(() => import('pages/app/home/Home')),
            menu: { icon: 'tachometer-alt' },
            roles: [
              ROLE_CLINIC_ADMINISTRATOR,
              ROLE_RECEPTIONIST,
              ROLE_PROFESSIONAL,
              ROLE_OTHER_HEALTH_PROFESSIONAL,
              ROLE_STUDENT
            ]
          },
          {
            roles: SUBSCRIPTION_MANAGER_ROLES,
            path: '/subscriptions',
            title: 'Assinatura',
            exact: true,
            component: asyncComponent(() =>
              import('pages/app/subscriptions/Subscription')
            )
          },
          {
            path: '/expired-license',
            title: 'Licença expirada',
            exact: true,
            component: asyncComponent(() =>
              import('pages/shared/LicenseSuspendInfo')
            )
          },
          {
            roles: [ROLE_CLINIC_ADMINISTRATOR, ROLE_FINANCIAL_ADMINISTRATOR],
            path: '/current-subscriptions',
            title: 'Minha assinatura',
            exact: true,
            component: asyncComponent(() =>
              import('pages/app/subscriptions/SubscriptionDetail')
            )
          },
          {
            path: '/schedule',
            title: 'Agenda',
            singularTitle: 'Agenda',
            roles: [
              ROLE_CLINIC_ADMINISTRATOR,
              ROLE_RECEPTIONIST,
              ROLE_PROFESSIONAL,
              ROLE_OTHER_HEALTH_PROFESSIONAL,
              ROLE_STUDENT
            ],
            component: asyncComponent(() =>
              import('pages/app/events/EventSchedule')
            ),
            menu: { icon: 'tachometer-alt' }
          },
          {
            path: '/recipient-care',
            title: 'Atendimento',
            singularTitle: 'Atendimento',
            exact: true,
            menu: { icon: 'users' },
            roles: [
              ROLE_CLINIC_ADMINISTRATOR,
              ROLE_RECEPTIONIST,
              ROLE_PROFESSIONAL,
              ROLE_OTHER_HEALTH_PROFESSIONAL,
              ROLE_STUDENT
            ],
            routes: [
              {
                path: '/analysis',
                title: 'Atendimentos concluídos',
                singularTitle: 'Análise de atendimentos concluídos',
                component: asyncComponent(() =>
                  import('pages/app/attendances/AttendanceAnalysis')
                ),
                menu: { icon: 'chart-line' }
              },
              {
                path: '/schedule-analysis',
                title: 'Atendimentos agendados',
                singularTitle: 'Análise de agendamentos',
                component: asyncComponent(() =>
                  import('pages/app/events/EventAnalysis')
                ),
                menu: { icon: 'chart-line' }
              },
              {
                path: '/profiles',
                title: 'Pacientes',
                exact: true,
                singularTitle: `${segmentFields.serviceRecipient}`,
                component: asyncComponent(() =>
                  import('pages/app/patients/PatientList')
                ),
                menu: { icon: 'users' },
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_RECEPTIONIST,
                  ROLE_PROFESSIONAL,
                  ROLE_OTHER_HEALTH_PROFESSIONAL,
                  ROLE_STUDENT
                ],
                routes: [
                  {
                    path: '/:id/history',
                    title: `${segmentFields.record}`,
                    exact: true,
                    singularTitle: `${segmentFields.record}`,
                    roles: [
                      ROLE_PROFESSIONAL,
                      ROLE_OTHER_HEALTH_PROFESSIONAL,
                      ROLE_STUDENT
                    ],
                    component: asyncComponent(() =>
                      import('pages/app/patients/PatientDetail')
                    )
                  }
                ]
              }
            ]
          },

          {
            path: '/relationship',
            title: 'Relacionamento',
            exact: true,
            singularTitle: 'Relacionamento',
            menu: { icon: 'users' },
            roles: [
              ROLE_CLINIC_ADMINISTRATOR,
              ROLE_PROFESSIONAL,
              ROLE_OTHER_HEALTH_PROFESSIONAL,
              ROLE_STUDENT
            ],
            routes: [
              {
                path: '/campaigns',
                title: 'Campanhas',
                resource: 'campaigns',
                singularTitle: 'Campanha',
                component: asyncComponent(() =>
                  import('pages/app/campaigns/CampaignList')
                ),
                menu: { icon: 'paper-plane' },
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_OTHER_HEALTH_PROFESSIONAL,
                  ROLE_PROFESSIONAL
                ]
              },
              {
                path: '/contacts',
                title: 'Contatos',
                resource: 'contacts',
                singularTitle: 'Contato',
                component: asyncComponent(() =>
                  import('pages/app/contacts/ContactList')
                ),
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_OTHER_HEALTH_PROFESSIONAL,
                  ROLE_PROFESSIONAL,
                  ROLE_STUDENT
                ],
                menu: { icon: 'user' }
              }
            ]
          },
          {
            path: '/attendances/:id',
            exact: true,
            title: `${segmentFields.record}`,
            singularTitle: `${segmentFields.record}`,
            roles: [
              ROLE_OTHER_HEALTH_PROFESSIONAL,
              ROLE_PROFESSIONAL,
              ROLE_STUDENT
            ],
            component: asyncComponent(() =>
              import('pages/app/attendances/AttendanceEdit')
            )
          },
          {
            path: '/attendances/:id/redirect',
            title: 'Redirecionamento',
            singularTitle: `${segmentFields.record}`,
            component: asyncComponent(() =>
              import('pages/shared/ReceitaDigitalRedirect')
            )
          },
          {
            path: '/patients/:id/history/redirect',
            title: 'Redirecionamento',
            singularTitle: `${segmentFields.record}`,
            component: asyncComponent(() =>
              import('pages/shared/ReceitaDigitalRedirect')
            )
          },

          {
            path: '/financial',
            title: 'Financeiro',
            singularTitle: 'Financeiro',
            exact: true,
            menu: { icon: 'users' },
            roles: [
              ROLE_CLINIC_ADMINISTRATOR,
              ROLE_FINANCIAL_ADMINISTRATOR,
              ROLE_FINANCIAL
            ],
            routes: [
              {
                path: '/financial-dashboard',
                title: 'Painel financeiro',
                singularTitle: 'Painel financeiro',
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR,
                  ROLE_FINANCIAL
                ],
                component: asyncComponent(() =>
                  import(
                    'pages/app/financialStatistic/FinancialStatisticDashboard'
                  )
                ),
                menu: { icon: 'chart-area' }
              },

              {
                path: '/financial-entries',
                title: 'Movimentações financeira',
                singularTitle: 'Movimentações financeira',
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR,
                  ROLE_FINANCIAL
                ],
                component: asyncComponent(() =>
                  import('pages/app/financialEntries/FinancialEntryList')
                ),
                menu: { icon: 'receipt' }
              },
              {
                path: '/accounts-receivables',
                title: 'Contas a Receber',
                singularTitle: 'Contas a Receber',
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR,
                  ROLE_FINANCIAL
                ],
                component: asyncComponent(() =>
                  import('pages/app/reports/AccountsReceivableReport')
                ),
                menu: { icon: 'hand-holding' }
              },
              {
                path: '/accounts-payable',
                title: 'Contas a Pagar',
                singularTitle: 'Contas a Pagar',
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR,
                  ROLE_FINANCIAL
                ],
                component: asyncComponent(() =>
                  import('pages/app/reports/AccountsPayableReport')
                ),
                menu: { icon: 'hand-holding-usd' }
              },
              {
                path: '/open-financial-exchanges',
                title: 'Conciliação bancária',
                singularTitle: 'Conciliação bancária',
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR,
                  ROLE_FINANCIAL
                ],
                component: asyncComponent(() =>
                  import(
                    'pages/app/openFinancialExchanges/OpenFinancialExchangeTab'
                  )
                ),
                menu: { icon: 'landmark' }
              },
              {
                path: '/bank-statement',
                title: 'Extrato Bancário',
                singularTitle: 'Extrato Bancário',
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR,
                  ROLE_FINANCIAL
                ],
                component: asyncComponent(() =>
                  import(
                    'pages/app/financialEntries/FinancialEntryBankStatement'
                  )
                ),
                menu: { icon: 'file-alt' }
              },
              {
                path: '/cash-book',
                title: 'Livro Caixa',
                singularTitle: 'Livro Caixa',
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR,
                  ROLE_FINANCIAL
                ],
                component: asyncComponent(() =>
                  import('pages/app/financialEntries/FinancialEntryCashBook')
                ),
                menu: { icon: 'book-open' }
              },
              {
                path: '/cash-flow',
                title: 'Fluxo de Caixa',
                singularTitle: 'Fluxo de Caixa',
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR,
                  ROLE_FINANCIAL
                ],
                component: asyncComponent(() =>
                  import('pages/app/financialEntries/FinancialEntryCashFlow')
                ),
                menu: { icon: 'chart-line' }
              },
              {
                path: '/balances',
                title: 'Análise por categoria',
                singularTitle: 'Análise por categoria',
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR,
                  ROLE_FINANCIAL
                ],
                component: asyncComponent(() =>
                  import('pages/app/checkingAccounts/CheckingAccountBalance')
                ),
                menu: { icon: 'chart-pie' }
              },
              {
                path: '/financial-income-statement',
                title: 'DRE Gerencial',
                singularTitle: 'DRE Gerencial',
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR,
                  ROLE_FINANCIAL
                ],
                component: asyncComponent(() =>
                  import(
                    'pages/app/checkingAccounts/CheckingAccountIncomeStatement'
                  )
                ),
                menu: { icon: 'chart-bar' }
              }
            ]
          },
          {
            path: '/stock',
            title: 'Estoque',
            exact: true,
            singularTitle: 'Estoque',
            menu: { icon: 'cubes' },
            hidden: (settings) => !settings.enableStock,
            roles: [ROLE_CLINIC_ADMINISTRATOR, ROLE_STOCK],
            routes: [
              {
                path: '/product-movements',
                title: 'Entrada e saída de produtos',
                resource: 'product-movements',
                singularTitle: 'Entrada e saída de produto',
                component: asyncComponent(() =>
                  import(
                    'pages/app/stocks/productMovements/ProductMovementList'
                  )
                ),
                menu: { icon: 'cubes' },
                roles: [ROLE_CLINIC_ADMINISTRATOR, ROLE_STOCK]
              },
              {
                path: '/products',
                title: 'Produtos',
                resource: 'products',
                singularTitle: 'Produto',
                component: asyncComponent(() =>
                  import('pages/app/stocks/products/ProductList')
                ),
                menu: { icon: 'box' },
                roles: [ROLE_CLINIC_ADMINISTRATOR, ROLE_STOCK]
              },
              {
                path: '/groups',
                title: 'Grupos',
                resource: 'groups',
                singularTitle: 'Grupo',
                component: asyncComponent(() =>
                  import('pages/app/stocks/groups/GroupList')
                ),
                menu: { icon: 'cubes' },
                roles: [ROLE_CLINIC_ADMINISTRATOR, ROLE_STOCK]
              },
              {
                path: '/brands',
                title: 'Marcas',
                resource: 'brands',
                singularTitle: 'Marca',
                component: asyncComponent(() =>
                  import('pages/app/stocks/brands/BrandList')
                ),
                menu: { icon: 'tags' },
                roles: [ROLE_CLINIC_ADMINISTRATOR, ROLE_STOCK]
              },
              {
                path: '/units',
                title: 'Unidades',
                resource: 'units',
                singularTitle: 'Unidade',
                component: asyncComponent(() =>
                  import('pages/app/stocks/units/UnitList')
                ),
                menu: { icon: 'ruler-horizontal' },
                roles: [ROLE_CLINIC_ADMINISTRATOR, ROLE_STOCK]
              },
              {
                path: '/product-suites',
                title: 'Kit de produtos',
                resource: 'product-suites',
                singularTitle: 'Kit de produto',
                component: asyncComponent(() =>
                  import('pages/app/stocks/products/ProductSuiteList')
                ),
                menu: { icon: 'boxes' },
                roles: [ROLE_CLINIC_ADMINISTRATOR, ROLE_STOCK]
              }
            ]
          },
          {
            path: '/settings',
            title: 'Configurações',
            component: (props) => <Settings {...props} />,
            hidden: () => true,
            isContainer: false,
            roles: [
              ROLE_CLINIC_ADMINISTRATOR,
              ROLE_FINANCIAL_ADMINISTRATOR,
              ROLE_PROFESSIONAL,
              ROLE_OTHER_HEALTH_PROFESSIONAL,
              ROLE_STUDENT,
              ROLE_FINANCIAL,
              ROLE_ACCOUNTANT,
              ROLE_RECEPTIONIST
            ],
            routes: [
              {
                roles: [ROLE_CLINIC_ADMINISTRATOR],
                path: '/geral-settings',
                title: 'Configurações gerais',
                singularTitle: 'Confiração geral',
                component: asyncComponent(() =>
                  import('pages/app/settings/GeralSettings')
                ),
                menu: { icon: 'cogs' }
              },
              {
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR,
                  ROLE_ACCOUNTANT
                ],
                path: '/company',
                title: 'Dados da empresa',
                singularTitle: 'Dados da empresa',
                component: asyncComponent(() =>
                  import('pages/shared/CurrentCompanyEdit')
                ),
                menu: { icon: 'building' }
              },
              {
                roles: [ROLE_CLINIC_ADMINISTRATOR],
                path: '/document-models',
                title: 'Modelos de documento',
                singularTitle: 'Modelo de documento',
                component: asyncComponent(() =>
                  import('pages/app/documentModels/DocumentModelList')
                ),
                menu: { icon: 'file-alt' }
              },
              getRecordRoutes(),
              {
                roles: [ROLE_CLINIC_ADMINISTRATOR],
                path: '/pricers',
                title: segmentFields.priceTables,
                singularTitle: segmentFields.priceTable,
                hidden: () => segmentFields.isHealthcareEnabled,
                component: asyncComponent(() =>
                  import('pages/app/pricingTables/PricingTableList')
                ),
                menu: { icon: 'funnel-dollar' }
              },
              getServiceRoutes(),
              {
                roles: [ROLE_CLINIC_ADMINISTRATOR],
                path: '/procedure-packages',
                title: 'Pacotes',
                singularTitle: 'Pacote',
                features: ['procedure_package_management'],
                component: asyncComponent(() =>
                  import('pages/app/procedures/ProcedurePackageList')
                ),
                menu: { icon: 'box' }
              },
              {
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR
                ],
                path: '/collaborators',
                title: 'Colaboradores',
                resource: 'collaborators',
                singularTitle: 'Colaborador',
                component: asyncComponent(() =>
                  import('pages/app/collaborators/CollaboratorList')
                ),
                menu: { icon: 'users' }
              },
              {
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR,
                  ROLE_FINANCIAL
                ],
                path: '/finances',
                title: 'Financeiro',
                component: () => null,
                menu: { icon: 'cash-register' },
                routes: [
                  {
                    roles: [
                      ROLE_CLINIC_ADMINISTRATOR,
                      ROLE_FINANCIAL_ADMINISTRATOR,
                      ROLE_FINANCIAL
                    ],
                    path: '/checking-accounts',
                    title: 'Contas',
                    singularTitle: 'Conta',
                    component: asyncComponent(() =>
                      import('pages/app/checkingAccounts/CheckingAccountList')
                    ),
                    menu: { icon: 'wallet' }
                  },
                  {
                    roles: [
                      ROLE_CLINIC_ADMINISTRATOR,
                      ROLE_FINANCIAL_ADMINISTRATOR,
                      ROLE_FINANCIAL
                    ],
                    path: '/management-accounts',
                    title: 'Contas DRE',
                    singularTitle: 'Conta DRE',
                    component: asyncComponent(() =>
                      import(
                        'pages/app/managementAccounts/ManagementAccountList'
                      )
                    ),
                    menu: { icon: 'wallet' }
                  },
                  {
                    roles: [
                      ROLE_CLINIC_ADMINISTRATOR,
                      ROLE_FINANCIAL_ADMINISTRATOR,
                      ROLE_FINANCIAL
                    ],
                    path: '/categories',
                    title: 'Categorias',
                    singularTitle: 'Categoria',
                    component: asyncComponent(() =>
                      import('pages/app/categories/CategoryList')
                    ),
                    menu: { icon: 'tags' }
                  },
                  {
                    roles: [
                      ROLE_CLINIC_ADMINISTRATOR,
                      ROLE_FINANCIAL_ADMINISTRATOR,
                      ROLE_FINANCIAL
                    ],
                    path: '/cost-centers',
                    title: 'Centro de Custos',
                    singularTitle: 'Centro de Custo',
                    component: asyncComponent(() =>
                      import('pages/app/costCenters/CostCenterList')
                    ),
                    menu: { icon: 'coins' }
                  },
                  {
                    roles: [
                      ROLE_CLINIC_ADMINISTRATOR,
                      ROLE_FINANCIAL_ADMINISTRATOR,
                      ROLE_FINANCIAL
                    ],
                    path: '/customers',
                    title: `${segmentFields.clients}`,
                    singularTitle: `${segmentFields.client}`,
                    component: asyncComponent(() =>
                      import('pages/app/customers/CustomerList')
                    ),
                    menu: { icon: 'user-friends' }
                  },
                  {
                    roles: [
                      ROLE_CLINIC_ADMINISTRATOR,
                      ROLE_FINANCIAL_ADMINISTRATOR,
                      ROLE_FINANCIAL
                    ],
                    path: '/suppliers',
                    title: 'Fornecedores',
                    singularTitle: 'Fornecedor',
                    component: asyncComponent(() =>
                      import('pages/app/suppliers/SupplierList')
                    ),
                    menu: { icon: 'truck-loading' }
                  },
                  {
                    roles: [
                      ROLE_CLINIC_ADMINISTRATOR,
                      ROLE_FINANCIAL_ADMINISTRATOR,
                      ROLE_FINANCIAL
                    ],
                    path: '/holidays',
                    title: 'Feriados',
                    singularTitle: 'Feriado',
                    component: asyncComponent(() =>
                      import('pages/app/holidays/HolidayList')
                    ),
                    menu: { icon: 'calendar' }
                  }
                ]
              },
              {
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR,
                  ROLE_ACCOUNTANT
                ],
                path: '/accountings',
                title: 'Contabilidade',
                singularTitle: 'Contablidades',
                component: asyncComponent(() =>
                  import('pages/app/accountings/AccountantTab')
                ),
                menu: { icon: 'calculator' }
              },
              {
                roles: [
                  ROLE_CLINIC_ADMINISTRATOR,
                  ROLE_FINANCIAL_ADMINISTRATOR,
                  ROLE_ACCOUNTANT
                ],
                path: '/invoice',
                title: 'Configurações das notas fiscais',
                singularTitle: 'Configuração da nota fiscal',
                component: asyncComponent(() =>
                  import('pages/app/invoices/InvoiceSettingsList')
                ),
                menu: { icon: 'file-invoice-dollar' }
              },
              {
                roles: [ROLE_CLINIC_ADMINISTRATOR, ROLE_RECEPTIONIST],
                path: '/platforms',
                title: 'Apps',
                singularTitle: 'App',
                component: asyncComponent(() =>
                  import('pages/app/platforms/PlatformList')
                ),
                menu: { icon: 'th-large' }
              }
            ]
          }
        ]
      },
      {
        path: '/',
        exact: true,
        redirectTo: '/app/'
      },
      {
        path: '/p',
        exact: true,
        redirectTo: '/p/licenses'
      }
    ]
  };
};

export default useConfig;
